// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// BULK-1: bulk-change OWLRX-OWNED fields on many selected Rx at once (queue tabs + custom
// view tabs). Scope is strictly the fields OwlRx writes — pharmacy custom fields
// (rx_custom_field_values) and editable WorkBench fields (rx_workbench). Pioneer-mirrored
// columns are NEVER offered: PioneerRx is the source of truth and OwlRx does not write there
// (pioneer/derived/retired workbench kinds are filtered out, same rule as the inline cell).
//
// Writes reuse the EXISTING per-Rx endpoints (PUT /rx/{id}/custom-fields and
// PUT /rx/{id}/workbench) — same authz, same audit trail, same anti-revert semantics as an
// inline edit; this dialog is only a fan-out. Ids must be TRUE rx_items.ids (CV-2a).
//
// BULK-3 (team request 2026-08-07): change MANY fields in one run. A row = one field + its
// value; add as many as needed. Per Rx this stays at most TWO requests no matter how many
// fields are picked — both write endpoints already accept a batch (`{values:[…]}` /
// `{fields:{…}}`), each with its own single MERGE pass and audit row. Sending one request
// per field would have multiplied fan-out against a 1-vCore tenant DB for no reason.
import { useEffect, useMemo, useState } from 'react'
import { Layers, Plus, X } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../common/SearchableSelect'
import { useWorkBenchDefs } from './WorkBench'
import { usePmsName } from '../../auth/AuthContext'
import { bulkWritePlan } from './bulkWritePlan'

const CLEAR = '__clear__'

/** One pending change: a chosen field and the value to write (CLEAR = blank it). */
type ChangeRow = { key: number; fieldKey: string; value: string }
let rowSeq = 0
const newRow = (): ChangeRow => ({ key: ++rowSeq, fieldKey: '', value: '' })

export default function BulkFieldEditor({ rxIds, wbIds, recordsTable, onClose, onApplied }: {
  /** TRUE rx_items.ids (never fill-grain display ids) of the selected rows. */
  /** PRESCRIPTION ids. Custom-field values are keyed on the prescription. */
  rxIds: string[]
  /**
   * The ids WORKBENCH writes go to, aligned index-for-index with rxIds. At fill grain that is
   * the FILL's id, because a WorkBench value belongs to one dispense; everywhere else it is the
   * same prescription id.
   *
   * The two lists exist separately because this dialog writes at TWO grains in one pass. The
   * header said "Ids must be TRUE rx_items.ids (CV-2a)" and one list served both, so every bulk
   * WorkBench edit on a fill row was written prescription-grain and unstamped - the same defect
   * fixed for the inline cells and the drawer, still live here because nothing pointed at it.
   * Omitted, it falls back to rxIds, which is correct for a prescription-grain list.
   */
  wbIds?: string[]
  /** BULK-2: the COMPLETE data of the checked rows, as the caller's grid shows them —
   *  rendered as a table at the bottom of the dialog so the user sees exactly which
   *  records the change will hit before applying. One cells entry per column, aligned
   *  with `columns`; a row that left the loaded window between selecting and opening
   *  renders as "…". Optional for backward compatibility. */
  recordsTable?: { columns: string[]; rows: Array<{ id: string; cells: Array<string | null> }> }
  onClose: () => void
  /** Called after a run finishes with at least one success — caller refreshes its rows. */
  onApplied: () => void
}) {
  const { name: pmsName } = usePmsName()
  const wbDefs = useWorkBenchDefs()
  const [cfDefs, setCfDefs] = useState<api.CustomFieldDef[]>([])
  useEffect(() => {
    let dead = false
    api.listCustomFieldDefs().then(r => { if (!dead) setCfDefs(r.items ?? []) }).catch(() => {})
    return () => { dead = true }
  }, [])

  // One flat picker: cf:<defGuid> | wb:<field_key>. Only writable kinds are listed.
  const fieldOptions = useMemo(() => {
    const cf = cfDefs
      .filter(d => (d as any).is_active !== false)
      .map(d => ({ value: `cf:${d.id}`, label: d.field_name, hint: 'Custom field' }))
    const wb = Object.values(wbDefs)
      .filter(d => !['pioneer', 'derived', 'retired'].includes(d.field_kind ?? ''))
      .map(d => ({ value: `wb:${d.field_key}`, label: d.display_name, hint: 'Workbench' }))
    return [...cf, ...wb].sort((a, b) => a.label.localeCompare(b.label))
  }, [cfDefs, wbDefs])

  const [rows, setRows] = useState<ChangeRow[]>([newRow()])
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<{ ok: number; failed: number } | null>(null)

  const patchRow = (key: number, patch: Partial<ChangeRow>) => {
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)))
    setResult(null)
  }
  const removeRow = (key: number) => setRows(rs => (rs.length === 1 ? [newRow()] : rs.filter(r => r.key !== key)))

  const defFor = (fieldKey: string) => ({
    cf: fieldKey.startsWith('cf:') ? cfDefs.find(d => d.id === fieldKey.slice(3)) : undefined,
    wb: fieldKey.startsWith('wb:') ? wbDefs[fieldKey.slice(3)] : undefined,
  })

  // Choice list for one field (configured options — the same lists the inline cells use).
  const valueOptionsFor = (fieldKey: string) => {
    const { cf, wb } = defFor(fieldKey)
    if (wb) return wb.values.filter(v => !(v as any).retired).map(v => ({ value: v.value, label: v.value }))
    if (cf?.field_type === 'dropdown' && cf.dropdown_options) {
      try {
        const arr = JSON.parse(cf.dropdown_options) as any[]
        return arr.map(o => (typeof o === 'string' ? o : o?.label)).filter(Boolean)
          .map((l: string) => ({ value: l, label: l }))
      } catch { return [] }
    }
    if (cf?.field_type === 'checkbox') return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]
    return null   // free-typed (text / number / date)
  }

  // A row counts only once it has a field AND a value (or an explicit clear).
  const readyRows = rows.filter(r => r.fieldKey !== '' && (r.value === CLEAR || r.value !== ''))
  const chosen = new Set(rows.map(r => r.fieldKey).filter(Boolean))
  const canApply = readyRows.length > 0 && rxIds.length > 0 && !running

  async function apply() {
    setRunning(true); setProgress(0); setResult(null)

    // Build BOTH payloads once — they are identical for every selected Rx.
    const cfValues: api.RxCustomFieldValue[] = []
    const wbFields: Record<string, string | null> = {}
    for (const r of readyRows) {
      const v = r.value === CLEAR ? null : r.value
      const { cf, wb } = defFor(r.fieldKey)
      if (cf) {
        const entry: api.RxCustomFieldValue = { field_id: cf.id }
        if (cf.field_type === 'date') entry.value_date = v
        else if (cf.field_type === 'number') entry.value_number = v == null || v === '' ? null : Number(v)
        else if (cf.field_type === 'checkbox') entry.value_bool = v == null ? null : v === 'true'
        else entry.value_text = v
        cfValues.push(entry)
      } else if (wb) wbFields[wb.field_key] = v
    }
    const wbKeys = Object.keys(wbFields)

    let ok = 0, failed = 0, done = 0
    // Fan out over Rx (not over fields), small concurrency — each Rx takes at most one
    // custom-fields call and one workbench call, keeping the same authz, audit trail and
    // anti-revert semantics as an inline edit. An Rx counts as updated only if ALL of its
    // writes succeeded; a partial Rx is reported failed so nobody assumes it went through.
    // Pair each prescription id with the id its WorkBench write must go to, so the two grains
    // cannot drift apart as the queue is drained by four workers.
    const queue = bulkWritePlan(rxIds, wbIds)
    const worker = async () => {
      while (queue.length > 0) {
        const { rxId, wbId, writeCustomFields } = queue.shift()!
        try {
          if (cfValues.length > 0 && writeCustomFields) await api.setRxCustomFieldValues(rxId, cfValues)
          if (wbKeys.length > 0) {
            const res = await api.apiFetch(`/rx/${wbId}/workbench`, {
              method: 'PUT', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ fields: wbFields }),
            })
            if (!res.ok) throw res
          }
          ok++
        } catch { failed++ }
        finally { done++; setProgress(done) }
      }
    }
    await Promise.all(Array.from({ length: Math.min(4, rxIds.length) }, worker))
    setRunning(false)
    setResult({ ok, failed })
    if (ok > 0) onApplied()
  }

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget && !running) onClose() }}>
      <div role="dialog" aria-label="Bulk change fields"
        // BULK-2/BULK-3: with the selected-rows table AND several field rows the dialog needs
        // real room — the point is to READ the records you are about to change. Near-full on a
        // laptop, capped so it never sprawls on a wide monitor. Without the table, a compact
        // column that still fits a field row comfortably.
        style={{ ...dialog, ...(recordsTable ? { maxWidth: 1240, width: '96%', maxHeight: '92vh', overflowY: 'auto' } : { maxWidth: 560 }) }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={16} /> Bulk change — {rxIds.length} prescription{rxIds.length === 1 ? '' : 's'}
        </h2>
        <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--color-text-soft)' }}>
          Only OwlRx-owned fields are offered (custom + workbench). Data mirrored from{' '}
          {pmsName} is read-only here — those change in {pmsName}.
        </p>

        {/* One row per field being changed. Every row is applied to every selected Rx. */}
        {rows.map((r, i) => {
          const opts = valueOptionsFor(r.fieldKey)
          const { cf } = defFor(r.fieldKey)
          const kind = cf?.field_type === 'date' ? 'date' : cf?.field_type === 'number' ? 'number' : 'text'
          const clearing = r.value === CLEAR
          return (
            <div key={r.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
              <div style={{ flex: '1 1 45%', minWidth: 0 }}>
                {i === 0 && <label style={label}>Field</label>}
                <SearchableSelect value={r.fieldKey}
                  onChange={v => patchRow(r.key, { fieldKey: v, value: '' })}
                  ariaLabel={`Field to change ${i + 1}`} placeholder="Choose a field…"
                  // A field already picked in another row is hidden here — one value per field.
                  options={fieldOptions.filter(o => o.value === r.fieldKey || !chosen.has(o.value))} />
              </div>
              <div style={{ flex: '1 1 45%', minWidth: 0 }}>
                {i === 0 && <label style={label}>New value</label>}
                {r.fieldKey === '' ? (
                  <div style={{ ...input, color: 'var(--color-text-mute)', background: 'var(--color-surface)' }}>—</div>
                ) : opts ? (
                  <SearchableSelect value={r.value} onChange={v => patchRow(r.key, { value: v })}
                    ariaLabel={`Value to apply ${i + 1}`} placeholder="Choose a value…"
                    options={[{ value: CLEAR, label: '— (clear the field)' }, ...opts]} />
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type={kind} value={clearing ? '' : r.value} disabled={clearing}
                      onChange={e => patchRow(r.key, { value: e.target.value })}
                      aria-label={`Value to apply ${i + 1}`} style={input} />
                    <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={clearing}
                        onChange={e => patchRow(r.key, { value: e.target.checked ? CLEAR : '' })} />
                      Clear
                    </label>
                  </div>
                )}
              </div>
              <button onClick={() => removeRow(r.key)} disabled={running}
                aria-label={`Remove field ${i + 1}`} title="Remove this field"
                style={{ ...btnIcon, visibility: rows.length === 1 && r.fieldKey === '' ? 'hidden' : 'visible' }}>
                <X size={14} />
              </button>
            </div>
          )
        })}

        <button onClick={() => setRows(rs => [...rs, newRow()])} disabled={running || rows.some(r => r.fieldKey === '')}
          style={btnAddField}>
          <Plus size={13} /> Add another field
        </button>

        {running && (
          <div style={{ marginTop: 12, fontSize: 12.5 }}>Applying… {progress}/{rxIds.length}</div>
        )}
        {result && (
          <div role="status" style={{ marginTop: 12, fontSize: 12.5, color: result.failed > 0 ? '#92400e' : '#166534' }}>
            {result.ok} updated{result.failed > 0 ? ` · ${result.failed} failed (unchanged — retry or edit inline)` : ''}.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} disabled={running} style={btnCancel}>{result ? 'Close' : 'Cancel'}</button>
          <button onClick={apply} disabled={!canApply} style={{ ...btnPrimary, opacity: canApply ? 1 : 0.5 }}>
            {running ? 'Applying…'
              : readyRows.length > 1 ? `Apply ${readyRows.length} changes to ${rxIds.length}`
              : `Apply to ${rxIds.length}`}
          </button>
        </div>

        {recordsTable && recordsTable.rows.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-soft)', marginBottom: 5 }}>
              Selected prescriptions ({recordsTable.rows.length})
            </div>
            {/* BULK-3: grows with the viewport instead of a fixed 240px — on a laptop this is
                ~3× the rows visible, which is the whole point of showing the records. */}
            <div style={{ maxHeight: '46vh', overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 7 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <thead>
                  <tr>
                    {recordsTable.columns.map(c => (
                      <th key={c} style={{ position: 'sticky', top: 0, background: 'var(--color-bg, #fff)', textAlign: 'left', fontWeight: 600, fontSize: 11, padding: '6px 10px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recordsTable.rows.map(r => (
                    <tr key={r.id}>
                      {r.cells.map((cell, ci) => (
                        <td key={ci} title={cell ?? undefined}
                          style={{ padding: '5px 10px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {cell == null || cell === '' ? '—' : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
}
const dialog: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', borderRadius: 10, padding: 20,
  width: '95%', maxWidth: 420, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
}
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }
const input: React.CSSProperties = {
  flex: 1, padding: '7px 10px', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
}
const btnPrimary: React.CSSProperties = {
  padding: '7px 14px', background: 'var(--color-accent, #2563eb)', color: '#fff',
  border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
}
const btnCancel: React.CSSProperties = {
  padding: '7px 14px', background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 13, color: 'var(--color-text-soft)', cursor: 'pointer',
}
const btnIcon: React.CSSProperties = {
  padding: '7px 8px', background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 6, color: 'var(--color-text-soft)', cursor: 'pointer', lineHeight: 0,
}
const btnAddField: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2,
  padding: '5px 10px', background: 'transparent', border: '1px dashed var(--color-border)',
  borderRadius: 6, fontSize: 12, color: 'var(--color-text-soft)', cursor: 'pointer',
}
